/**
 * 把"画了框的那一页图纸"渲染成一张图片（客户版 / 工厂版打印用）。
 * 全程在浏览器里做：PDFium 渲染页 → 在同一张 canvas 上画框 → 导出 dataURL。
 *
 * **只画框，不画编号**（2026-10-16 老板要求：最终图纸上不要那几个 A01/A02 标签）。
 * 框的颜色仍然代表柜型，工厂版下面配图例；编号对应关系看报价单的条目表。
 * 工作台画布（`PdfCanvas.tsx`）上的编号不在此列，编辑时还要用。
 */

import { boxColorFor } from "./cabinet-colors";
import { renderPageToCanvas } from "./pdf";

export type AnnotatedBox = {
  /** 归一化坐标 0..1 */
  x: number;
  y: number;
  w: number;
  h: number;
  label: string;
  /** 柜型：决定框的颜色（和画布上一致） */
  cabinetType?: string;
  rotationDeg?: number;
};

export type AnnotatedPage = { dataUrl: string; width: number; height: number };

const FALLBACK_COLOR = "#2563eb";

export async function renderAnnotatedPage(options: {
  url: string;
  isImage: boolean;
  pageIndex: number;
  boxes: AnnotatedBox[];
  targetWidth?: number;
}): Promise<AnnotatedPage> {
  const targetWidth = options.targetWidth ?? 1500;
  const canvas = window.document.createElement("canvas");

  if (options.isImage) {
    const image = await loadImage(options.url);
    const scale = Math.min(1, targetWidth / image.naturalWidth);
    canvas.width = Math.round(image.naturalWidth * scale);
    canvas.height = Math.round(image.naturalHeight * scale);
    const context = canvas.getContext("2d");
    if (!context) throw new Error("画布不可用");
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
  } else {
    await renderPageToCanvas({
      url: options.url,
      pageIndex: options.pageIndex,
      canvas,
      cssWidth: targetWidth,
      devicePixelRatio: 1,
    });
  }

  const context = canvas.getContext("2d");
  if (!context) throw new Error("画布不可用");
  const width = canvas.width;
  const height = canvas.height;

  // 只画框：线宽跟着页面像素走，纸上看得出边界就够了
  context.lineWidth = Math.max(2, Math.round(width / 700));

  options.boxes.forEach((box) => {
    const color = boxColorFor(box.cabinetType);
    const px = box.x * width;
    const py = box.y * height;
    const pw = box.w * width;
    const ph = box.h * height;
    const rotation = ((box.rotationDeg ?? 0) * Math.PI) / 180;

    context.save();
    context.translate(px + pw / 2, py + ph / 2);
    context.rotate(rotation);
    context.strokeStyle = color.stroke || FALLBACK_COLOR;
    context.fillStyle = color.fill;
    context.fillRect(-pw / 2, -ph / 2, pw, ph);
    context.strokeRect(-pw / 2, -ph / 2, pw, ph);
    context.restore();
  });

  return { dataUrl: canvas.toDataURL("image/jpeg", 0.88), width, height };
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "same-origin";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("图片加载失败"));
    image.src = url;
  });
}
